import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  ArrowUpRight, ArrowRight, Radar, Brain, Network, Boxes, Glasses, Zap,
  Cpu, LayoutDashboard, Orbit, Wifi, MonitorCog, CheckCircle2,
  Sparkles, Workflow, Layers, ShieldCheck, Target, Repeat,
  Factory, HardHat, Truck, Building2, Bolt, Briefcase, ShieldAlert, Warehouse, Users, Stethoscope,
} from "lucide-react";
import {
  IMAGES, INTELLIGENCE_FRAMEWORK, WHAT_WE_BUILD, OUTCOMES,
  CAPABILITY_CHIPS, COMPLEX_USE_CASES, WHY_NGXP,
} from "@/lib/data";
import API from "@/lib/api";

const ICONS = {
  Radar, Brain, Network, Boxes, Glasses, Zap,
  Cpu, LayoutDashboard, Orbit, Wifi, MonitorCog,
  Sparkles, Workflow, Layers, ShieldCheck, Target, Repeat,
  Factory, HardHat, Truck, Building2, Bolt, Briefcase, ShieldAlert, Warehouse, Users, Stethoscope,
};

// Build an editable-content lookup with fallbacks. Keeps the visual design identical
// when API is unreachable or fields are blank.
function buildLookup(sections) {
  const map = {};
  for (const s of sections) map[s.key] = { content: s.content || {}, enabled: s.enabled !== false };
  const get = (key, field, fallback) => {
    const v = map[key]?.content?.[field];
    if (v === undefined || v === null || v === "") return fallback;
    if (Array.isArray(v) && v.length === 0) return fallback;
    return v;
  };
  const enabled = (key) => (map[key] ? map[key].enabled : true);
  return { get, enabled };
}

export default function Home() {
  const [sections, setSections] = useState([]);

  useEffect(() => {
    axios.get(`${API}/pages/home/sections`).then(({ data }) => {
      if (Array.isArray(data)) setSections(data);
    }).catch(() => {});
  }, []);

  const { get, enabled } = buildLookup(sections);
  void IMAGES;

  // Hero
  const heroOverline = get("hero", "overline", "Next Generation Intelligence Systems");
  const heroTitlePre = get("hero", "title_pre", "Engineering ");
  const heroTitleHighlight = get("hero", "title_highlight", "Self-Evolving");
  const heroTitlePost = get("hero", "title_post", " Intelligence Systems for Complex Enterprise Operations");
  const heroP1 = get("hero", "paragraph1", "NGXP Technologies combines AI, real-time data, IoT, embedded systems, advanced 3D simulation, XR, digital twins, and AI-native enterprise platforms to help organizations gain real-time visibility, predict risks, automate workflows, optimize performance, and continuously improve operations.");
  const heroP2 = get("hero", "paragraph2", "We build intelligent systems that connect real-world operations with digital intelligence — helping enterprises understand what is happening, why it is happening, what may happen next, and what action should be taken.");
  const heroCtaPrimaryLabel = get("hero", "cta_primary_label", "Discuss a Strategic Project");
  const heroCtaPrimaryLink = get("hero", "cta_primary_link", "/contact#contact-form");
  const heroCtaSecondaryLabel = get("hero", "cta_secondary_label", "Explore Our Capabilities");
  const heroCtaSecondaryLink = get("hero", "cta_secondary_link", "/services");
  const heroVideo = get("hero", "video_url", "/NGXP-LOGO_RESIZED.mp4");
  const heroBg = get("hero", "bg_image", "/bg.gif");
  const heroStats = get("hero", "stats", [
    { key: "AI-native", value: "Architecture" },
    { key: "Real-time", value: "Visibility" },
    { key: "10x", value: "Improvement potential" },
  ]);
  const heroBadgeLabel = get("hero", "badge_label", "Live");
  const heroBadgeText = get("hero", "badge_text", "Operational Intelligence");

  // Section 2
  const s2Overline = get("operational-intelligence", "overline", "The Shift");
  const s2TitlePre = get("operational-intelligence", "title_pre", "Enterprises Need More Than Software. They Need ");
  const s2TitleHighlight = get("operational-intelligence", "title_highlight", "Operational Intelligence.");
  const s2Paragraph = get("operational-intelligence", "paragraph", "Organizations today manage complex operations across people, assets, workflows, safety, productivity, and decision-making. Yet many still depend on disconnected systems, delayed reports, manual monitoring, and reactive decisions.");
  const s2CardTitle = get("operational-intelligence", "card_title", "Leadership teams need faster answers to:");
  const s2Questions = get("operational-intelligence", "questions", [
    "What is happening right now?",
    "Where are the risks?",
    "What is slowing performance?",
    "What needs immediate attention?",
  ]);
  const s2AnswerLabel = get("operational-intelligence", "answer_label", "The NGXP Answer");
  const s2AnswerText = get("operational-intelligence", "answer_text", "NGXP builds intelligence systems that help organizations see, understand, decide and act faster.");

  // Section 3
  const s3Overline = get("connected-systems", "overline", "Intelligent Operational Ecosystems");
  const s3TitlePre = get("connected-systems", "title_pre", "NGXP Converts Complex Operations into ");
  const s3TitleHighlight = get("connected-systems", "title_highlight", "Intelligent, Connected, and Continuously Improving Systems");
  const s3P1 = get("connected-systems", "paragraph1", "NGXP Technologies builds future-ready intelligence systems that connect enterprise data, real-world activity, AI intelligence, simulation, digital twins, dashboards, and workflow automation into one unified operational ecosystem.");
  const s3P2 = get("connected-systems", "paragraph2", "Our systems are designed to help organizations monitor operations in real time, identify patterns, predict risks, simulate scenarios, guide decisions, automate workflows, and improve over time through real-world usage.");
  const s3ConcPre = get("connected-systems", "conclusion_pre", "We help organizations move from ");
  const s3ConcOld = get("connected-systems", "conclusion_old", "static digital systems");
  const s3ConcMid = get("connected-systems", "conclusion_mid", " to ");
  const s3ConcNew = get("connected-systems", "conclusion_new", "intelligent operational ecosystems.");
  const s3Tiles = get("connected-systems", "tiles", [
    { img: "/Real-world-data.gif", label: "Real-world data" },
    { img: "/Ai_Intelligence.gif", label: "AI intelligence" },
    { img: "/digital_twin.gif", label: "Digital twins" },
    { img: "/Decision-command_1080.gif", label: "Decision command" },
  ]);

  // Framework
  const s4Overline = get("framework", "overline", "Framework");
  const s4TitlePre = get("framework", "title_pre", "The NGXP ");
  const s4TitleHighlight = get("framework", "title_highlight", "Intelligence Framework");
  const s4Paragraph = get("framework", "paragraph", "Six interconnected layers that convert real-world activity into intelligent, continuously improving operations.");
  const s4Cards = get("framework", "cards", INTELLIGENCE_FRAMEWORK);

  // What we build
  const s5Overline = get("what-we-build", "overline", "What We Build");
  const s5TitlePre = get("what-we-build", "title_pre", "Six categories of ");
  const s5TitleHighlight = get("what-we-build", "title_highlight", "intelligent systems");
  const s5TitlePost = get("what-we-build", "title_post", " designed to transform operations.");
  const s5Cards = get("what-we-build", "cards", WHAT_WE_BUILD);

  // Outcomes
  const s6Overline = get("outcomes", "overline", "Business Impact");
  const s6TitlePre = get("outcomes", "title_pre", "Designed to Unlock ");
  const s6TitleHighlight = get("outcomes", "title_highlight", "10x Operational Improvement");
  const s6TitlePost = get("outcomes", "title_post", " Potential");
  const s6Paragraph = get("outcomes", "paragraph", "Traditional systems support operations. NGXP systems are designed to understand, optimize, and improve operations over time.");
  const s6Image = get("outcomes", "image_url", "/Designed-to-unlock.jpg");
  const s6Items = get("outcomes", "items", OUTCOMES);

  // Capabilities
  const s7Overline = get("capabilities", "overline", "Capabilities");
  const s7TitlePre = get("capabilities", "title_pre", "Built from ");
  const s7TitleHighlight = get("capabilities", "title_highlight", "Real Enterprise Challenges");
  const s7Paragraph = get("capabilities", "paragraph", "NGXP's capabilities are shaped by practical, high-complexity enterprise requirements across operations, safety, training, monitoring, reporting, and decision-making.");
  const s7ChipsLabel = get("capabilities", "chips_label", "Capability Areas");
  const s7Chips = get("capabilities", "chips", CAPABILITY_CHIPS);

  // Built for
  const s8Overline = get("built-for", "overline", "Built For");
  const s8TitlePre = get("built-for", "title_pre", "Organizations Managing ");
  const s8TitleHighlight = get("built-for", "title_highlight", "Complex Operations");
  const s8Paragraph = get("built-for", "paragraph", "NGXP is designed for organizations where operations are complex, high-value, safety-sensitive, asset-heavy, data-intensive, or difficult to monitor in real time.");
  const s8Items = get("built-for", "items", COMPLEX_USE_CASES);

  // Why NGXP
  const s9Overline = get("why-ngxp", "overline", "Why NGXP");
  const s9TitlePre = get("why-ngxp", "title_pre", "Why ");
  const s9TitleHighlight = get("why-ngxp", "title_highlight", "NGXP Technologies");
  const s9Cards = get("why-ngxp", "cards", WHY_NGXP);

  // Transformation
  const s10Overline = get("transformation", "overline", "The Next Evolution");
  const s10Image = get("transformation", "image_url", "/intelligent-transformation.jpg");
  const s10TitlePre = get("transformation", "title_pre", "From Digital Transformation to ");
  const s10TitleHighlight = get("transformation", "title_highlight", "Intelligent Transformation");
  const s10Paragraph = get("transformation", "paragraph", "Digital transformation helped organizations move from paper to software. The next evolution is intelligent transformation — where systems understand operations, predict risks, guide decisions, automate workflows, and continuously improve performance.");
  const s10Timeline = get("transformation", "timeline", [
    { label: "Yesterday", value: "Paper" },
    { label: "Today", value: "Software" },
    { label: "Tomorrow", value: "Intelligence" },
  ]);

  // Final CTA
  const s11Title = get("final-cta", "title", "Ready to Build Your Next-Generation Intelligence System?");
  const s11Paragraph = get("final-cta", "paragraph", "Whether you are planning an AI-native enterprise platform, digital twin, simulation system, operational intelligence solution, immersive training system, or strategic technology partnership, NGXP can help convert complex operational challenges into intelligent, scalable, and future-ready systems.");
  const s11CtaPrimaryLabel = get("final-cta", "cta_primary_label", "Start a Strategic Discussion");
  const s11CtaPrimaryLink = get("final-cta", "cta_primary_link", "/contact#contact-form");
  const s11CtaSecondaryLabel = get("final-cta", "cta_secondary_label", "Contact NGXP Technologies");
  const s11CtaSecondaryLink = get("final-cta", "cta_secondary_link", "/contact");
  const s11Tagline = get("final-cta", "tagline", "NGXP Technologies — Engineering self-evolving intelligence systems for complex enterprise operations.");

  return (
    <div data-testid="home-page">
      {/* ============= HERO ============= */}
      {enabled("hero") && (
      <section className="relative overflow-hidden bg-white" data-testid="hero-section">
        {/* Background GIF */}
        <img
          src={heroBg}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover opacity-15 pointer-events-none"
        />
        <div className="absolute inset-0 grid-pattern pointer-events-none" />
        <div className="glow-blob bg-[#7B2CBF]/25 w-[600px] h-[600px] -top-40 -right-32 pulse-glow" />
        <div className="glow-blob bg-[#00A8E8]/20 w-[500px] h-[500px] -bottom-32 -left-20 pulse-glow" />

        <div className="container-ngxp relative z-10 pt-6 md:pt-10 lg:pt-12 pb-14 md:pb-16 lg:pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-6">
              <span className="overline" data-testid="hero-overline">{heroOverline}</span>
              <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.05] tracking-tight text-[#0A0B10]">
                {heroTitlePre}<span className="gradient-text-glow">{heroTitleHighlight}</span>{heroTitlePost}
              </h1>
              <p className="mt-6 text-lg md:text-xl text-gray-600 leading-relaxed max-w-2xl">
                {heroP1}
              </p>
              <p className="mt-5 text-base text-gray-500 max-w-2xl leading-relaxed">
                {heroP2}
              </p>
              <div className="mt-9 flex flex-wrap gap-3" data-testid="hero-ctas">
                <Link to={heroCtaPrimaryLink} className="btn-primary-gradient" data-testid="hero-cta-primary">
                  {heroCtaPrimaryLabel} <ArrowUpRight className="w-4 h-4" />
                </Link>
                <Link to={heroCtaSecondaryLink} className="btn-secondary-light" data-testid="hero-cta-secondary">
                  {heroCtaSecondaryLabel} <ArrowRight className="w-4 h-4" />
                </Link>
              </div>

              <div className="mt-12 grid grid-cols-3 max-w-md gap-6">
                {heroStats.map((s, i) => (
                  <div key={i}>
                    <div className="text-xl md:text-2xl font-display font-bold text-[#0A0B10]">{s.key}</div>
                    <div className="text-xs text-gray-500 mt-1">{s.value}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-6">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-tr from-[#00A8E8]/30 via-[#7B2CBF]/30 to-[#FF007F]/30 rounded-[2rem] blur-2xl" />
                <div className="relative rounded-[2rem] overflow-hidden border border-gray-100 shadow-2xl shadow-purple-500/10 blob-float">
                  <video
                    src={heroVideo}
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="w-full h-[320px] md:h-[380px] object-contain bg-black"
                  />
                </div>
                <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-4 shadow-2xl border border-gray-100 hidden md:block">
                  <div className="flex items-center gap-3">
                    <div className="icon-badge !w-10 !h-10 !rounded-xl !mb-0"><Brain className="w-5 h-5" /></div>
                    <div>
                      <div className="text-xs text-gray-500">{heroBadgeLabel}</div>
                      <div className="text-sm font-semibold">{heroBadgeText}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* ============= SECTION 2 — Operational Intelligence ============= */}
      {enabled("operational-intelligence") && (
      <section className="bg-[#F8F9FA] py-14 md:py-20" data-testid="section-operational-intelligence">
        <div className="container-ngxp">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-6 reveal">
              <span className="overline">{s2Overline}</span>
              <h2 className="font-display text-4xl md:text-5xl font-bold leading-tight text-[#0A0B10]">
                {s2TitlePre}<span className="gradient-text-glow">{s2TitleHighlight}</span>
              </h2>
              <p className="mt-6 text-gray-600 leading-relaxed">
                {s2Paragraph}
              </p>
            </div>
            <div className="lg:col-span-6 reveal">
              <div className="ngxp-card">
                <h3 className="font-display text-xl font-semibold mb-5">{s2CardTitle}</h3>
                <ul className="space-y-3">
                  {s2Questions.map((q, i) => (
                    <li key={i} className="flex items-start gap-3 text-gray-700">
                      <CheckCircle2 className="w-5 h-5 text-[#7B2CBF] mt-0.5 shrink-0" />
                      <span>{q}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 p-5 rounded-xl bg-gradient-to-r from-[#00A8E8]/10 via-[#7B2CBF]/10 to-[#FF007F]/10 border border-[#7B2CBF]/20">
                  <div className="text-xs uppercase tracking-[0.2em] font-bold gradient-text mb-1">{s2AnswerLabel}</div>
                  <p className="text-[#0A0B10] font-medium">{s2AnswerText}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* ============= SECTION 3 — Connected Systems ============= */}
      {enabled("connected-systems") && (
      <section className="bg-white py-14 md:py-20" data-testid="section-connected-systems">
        <div className="container-ngxp">
          <div className="max-w-4xl mx-auto text-center reveal">
            <span className="overline">{s3Overline}</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#0A0B10]">
              {s3TitlePre}<span className="gradient-text-glow">{s3TitleHighlight}</span>
            </h2>
            <p className="mt-6 text-lg text-gray-600 leading-relaxed">
              {s3P1}
            </p>
            <p className="mt-5 text-lg text-gray-600 leading-relaxed">
              {s3P2}
            </p>
            <p className="mt-5 font-display text-xl text-[#0A0B10] font-medium">
              {s3ConcPre}<span className="text-gray-400">{s3ConcOld}</span>{s3ConcMid}<span className="gradient-text-glow font-semibold">{s3ConcNew}</span>
            </p>
          </div>
          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-6 reveal">
            {s3Tiles.map((b, i) => (
              <div key={i} className="relative rounded-2xl overflow-hidden aspect-square border border-gray-100 group">
                <img src={b.img} alt={b.label} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B10]/80 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4 text-white text-sm font-semibold">{b.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* ============= SECTION 4 — NGXP Intelligence Framework (6 cards) ============= */}
      {enabled("framework") && (
      <section className="bg-[#F8F9FA] py-14 md:py-20" data-testid="section-framework">
        <div className="container-ngxp">
          <div className="max-w-3xl reveal">
            <span className="overline">{s4Overline}</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#0A0B10]">
              {s4TitlePre}<span className="gradient-text-glow">{s4TitleHighlight}</span>
            </h2>
            <p className="mt-5 text-gray-600 text-lg">{s4Paragraph}</p>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {s4Cards.map((c, i) => {
              const Icon = ICONS[c.icon] || Brain;
              return (
                <div key={i} className="ngxp-card reveal" data-testid={`framework-card-${i}`}>
                  <div className="flex items-start justify-between">
                    <div className="icon-badge"><Icon className="w-7 h-7" /></div>
                    <span className="text-xs font-bold tracking-[0.18em] uppercase text-gray-400">0{i + 1}</span>
                  </div>
                  <h3 className="font-display text-xl font-semibold mt-1">{c.layer}</h3>
                  <p className="text-sm font-medium gradient-text-glow mt-1">{c.title}</p>
                  <p className="text-gray-600 text-sm mt-4 leading-relaxed">{c.desc}</p>
                  <div className="mt-5 pt-5 border-t border-gray-100">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-gray-400 font-bold mb-1">Outcome</div>
                    <p className="text-sm text-[#0A0B10] font-medium">{c.outcome}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      )}

      {/* ============= SECTION 5 — What We Build (dark) ============= */}
      {enabled("what-we-build") && (
      <section className="bg-[#0A0B10] text-white py-14 md:py-20 relative overflow-hidden" data-testid="section-what-we-build">
        <div className="absolute inset-0 dark-grid-pattern opacity-50 pointer-events-none" />
        <div className="glow-blob bg-[#7B2CBF]/30 w-[600px] h-[600px] -top-40 -right-40 pulse-glow" />
        <div className="container-ngxp relative z-10">
          <div className="max-w-3xl reveal">
            <span className="overline">{s5Overline}</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight">
              {s5TitlePre}<span className="gradient-text-glow">{s5TitleHighlight}</span>{s5TitlePost}
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {s5Cards.map((s, i) => {
              const Icon = ICONS[s.icon] || Cpu;
              return (
                <div key={i} className="ngxp-glass-card flex flex-col reveal" data-testid={`build-card-${i}`}>
                  <div className="icon-badge-dark"><Icon className="w-7 h-7" /></div>
                  <h3 className="font-display text-xl font-semibold">{s.title}</h3>
                  <p className="text-sm text-white/65 mt-3 leading-relaxed">{s.desc}</p>
                  <ul className="mt-5 space-y-2">
                    {(s.features || []).map((f, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-white/75">
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-gradient-to-r from-[#7B2CBF] to-[#FF007F] shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      )}

      {/* ============= SECTION 6 — Outcomes ============= */}
      {enabled("outcomes") && (
      <section className="bg-white py-14 md:py-20" data-testid="section-outcomes">
        <div className="container-ngxp">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5 reveal">
              <span className="overline">{s6Overline}</span>
              <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#0A0B10] leading-tight">
                {s6TitlePre}<span className="gradient-text-glow">{s6TitleHighlight}</span>{s6TitlePost}
              </h2>
              <p className="mt-6 text-gray-600 leading-relaxed">
                {s6Paragraph}
              </p>
              <div className="mt-8 rounded-2xl overflow-hidden border border-gray-100 shadow-xl">
                <img src={s6Image} alt="Designed to unlock operational improvement" className="w-full h-64 object-cover" />
              </div>
            </div>
            <div className="lg:col-span-7 reveal">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {s6Items.map((o, i) => (
                  <div key={i} className="flex gap-3 items-start p-5 rounded-xl bg-[#F8F9FA] border border-gray-100 hover:border-[#7B2CBF]/30 transition-colors" data-testid={`outcome-${i}`}>
                    <div className="w-9 h-9 shrink-0 rounded-lg bg-gradient-to-br from-[#00A8E8] to-[#7B2CBF] text-white flex items-center justify-center text-xs font-bold">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <p className="text-sm text-[#0A0B10] font-medium leading-relaxed">{o}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* ============= SECTION 7 — Capabilities Chips ============= */}
      {enabled("capabilities") && (
      <section className="bg-[#F8F9FA] py-14 md:py-18" data-testid="section-capabilities">
        <div className="container-ngxp">
          <div className="max-w-3xl reveal">
            <span className="overline">{s7Overline}</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#0A0B10]">
              {s7TitlePre}<span className="gradient-text-glow">{s7TitleHighlight}</span>
            </h2>
            <p className="mt-5 text-lg text-gray-600 leading-relaxed">
              {s7Paragraph}
            </p>
          </div>
          <p className="mt-10 text-xs uppercase tracking-[0.2em] font-bold text-gray-400 reveal">{s7ChipsLabel}</p>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 reveal">
            {s7Chips.map((c, i) => (
              <div key={i} className="ngxp-card" data-testid={`chip-${i}`}>
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-2 h-2 rounded-full bg-gradient-to-r from-[#7B2CBF] to-[#FF007F] flex-shrink-0" />
                  <h3 className="font-display text-base font-semibold text-[#0A0B10]">{c.title}</h3>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* ============= SECTION 8 — Built For ============= */}
      {enabled("built-for") && (
      <section className="bg-white py-14 md:py-20" data-testid="section-built-for">
        <div className="container-ngxp grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 reveal">
            <span className="overline">{s8Overline}</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#0A0B10] leading-tight">
              {s8TitlePre}<span className="gradient-text-glow">{s8TitleHighlight}</span>
            </h2>
            <p className="mt-6 text-gray-600 leading-relaxed">
              {s8Paragraph}
            </p>
          </div>
          <div className="lg:col-span-6 reveal">
            <div className="grid grid-cols-2 gap-3">
              {s8Items.map((u, i) => (
                <div key={i} className="ngxp-card !p-5 !rounded-xl" data-testid={`usecase-${i}`}>
                  <div className="text-xs text-gray-400 font-mono">{String(i + 1).padStart(2, "0")}</div>
                  <p className="mt-1 text-sm font-medium text-[#0A0B10]">{u}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      )}

      {/* ============= SECTION 9 — Why NGXP ============= */}
      {enabled("why-ngxp") && (
      <section className="bg-[#0A0B10] text-white py-14 md:py-20 relative overflow-hidden" data-testid="section-why-ngxp">
        <div className="absolute inset-0 dark-grid-pattern opacity-40 pointer-events-none" />
        <div className="glow-blob bg-[#FF007F]/20 w-[500px] h-[500px] top-20 -left-40 pulse-glow" />
        <div className="glow-blob bg-[#00A8E8]/20 w-[500px] h-[500px] bottom-0 -right-40 pulse-glow" />
        <div className="container-ngxp relative z-10">
          <div className="max-w-3xl reveal">
            <span className="overline">{s9Overline}</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight">
              {s9TitlePre}<span className="gradient-text-glow">{s9TitleHighlight}</span>
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {s9Cards.map((w, i) => {
              const Icon = ICONS[w.icon] || Sparkles;
              return (
                <div key={i} className="ngxp-glass-card reveal" data-testid={`why-card-${i}`}>
                  <div className="icon-badge-dark"><Icon className="w-7 h-7" /></div>
                  <h3 className="font-display text-lg font-semibold">{w.title}</h3>
                  <p className="text-sm text-white/65 mt-3 leading-relaxed">{w.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      )}

      {/* ============= SECTION 10 — Intelligent Transformation ============= */}
      {enabled("transformation") && (
      <section className="bg-white py-14 md:py-20" data-testid="section-transformation">
        <div className="container-ngxp grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 reveal">
            <div className="relative rounded-3xl overflow-hidden border border-gray-100 shadow-2xl">
              <img src={s10Image} alt="Intelligent transformation" className="w-full h-[420px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#0A0B10]/40 via-transparent to-transparent" />
            </div>
          </div>
          <div className="lg:col-span-6 reveal">
            <span className="overline">{s10Overline}</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#0A0B10] leading-tight">
              {s10TitlePre}<span className="gradient-text-glow">{s10TitleHighlight}</span>
            </h2>
            <p className="mt-6 text-gray-600 leading-relaxed">
              {s10Paragraph}
            </p>
            <div className="mt-8 grid grid-cols-3 gap-3">
              {s10Timeline.map((t, i) => (
                <div key={i} className={`p-5 rounded-xl border ${i === s10Timeline.length - 1 ? "bg-gradient-to-br from-[#00A8E8]/10 via-[#7B2CBF]/10 to-[#FF007F]/10 border-[#7B2CBF]/30" : "bg-[#F8F9FA] border-gray-100"}`}>
                  <div className="text-xs font-bold uppercase tracking-[0.18em] text-gray-400">{t.label}</div>
                  <div className={`mt-1 font-display font-semibold ${i === s10Timeline.length - 1 ? "gradient-text" : "text-[#0A0B10]"}`}>{t.value}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      )}

      {/* ============= SECTION 11 — Final CTA ============= */}
      {enabled("final-cta") && (
      <section className="relative py-14 md:py-20 overflow-hidden" data-testid="section-final-cta">
        <div className="absolute inset-0 bg-gradient-to-br from-[#00A8E8] via-[#7B2CBF] to-[#FF007F]" />
        <div className="absolute inset-0 dark-grid-pattern opacity-40" />
        <div className="container-ngxp relative z-10 text-center text-white">
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight max-w-4xl mx-auto leading-tight">
            {s11Title}
          </h2>
          <p className="mt-6 text-lg text-white/85 max-w-3xl mx-auto leading-relaxed">
            {s11Paragraph}
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4" data-testid="final-cta-buttons">
            <Link to={s11CtaPrimaryLink} className="inline-flex items-center gap-2 rounded-full bg-white text-[#0A0B10] px-8 py-4 text-sm font-semibold hover:-translate-y-0.5 transition-transform shadow-2xl" data-testid="final-cta-discuss">
              {s11CtaPrimaryLabel} <ArrowUpRight className="w-4 h-4" />
            </Link>
            <Link to={s11CtaSecondaryLink} className="btn-secondary-dark !border-white/40 hover:!bg-white/10" data-testid="final-cta-contact">
              {s11CtaSecondaryLabel} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <p className="mt-12 text-sm text-white/70 italic">
            {s11Tagline}
          </p>
        </div>
      </section>
      )}
    </div>
  );
}
